import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Navigate,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { useSession } from '../session';
import { useWorkspace } from '../workspace';
import { AppShell } from '../components/AppShell';
import { SetupGate } from '../components/SetupGate';
import { LoginScreen } from './login';
import { OnboardingScreen } from './-onboarding';
import styles from '../styles/shared.module.css';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({ component: RootComponent });

/** Routes that stay reachable before this browser has a vault unlocked. */
const vaultlessRoutes = new Set(['/shared', '/settings']);

function Splash({ children }: { children: string }) {
  return (
    <div className={styles.splash}>
      <div className={styles.splashInner}>
        <div className={styles.spinner} />
        {children}
      </div>
    </div>
  );
}

function RootComponent() {
  const { user, loading, error } = useSession();
  const { workspace, loading: workspaceLoading } = useWorkspace();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const connectFlow = pathname === '/connect' || pathname === '/device';

  if (loading) return <Splash>Opening your trove…</Splash>;
  if (error) return <div className={styles.splash}>Trove could not connect: {error.message}</div>;
  if (!user) return <LoginScreen />;
  if (!user.demo && !user.onboardingComplete && !connectFlow) return <OnboardingScreen />;
  // `/login` is only a signed-out entry URL; never mount it inside the app shell.
  if (pathname === '/login') return <Navigate to="/" replace />;
  if (connectFlow) return <Outlet />;
  // Demo sessions seed or join a vault from the Files route, so only real accounts gate here.
  if (!user.demo && !workspace && !vaultlessRoutes.has(pathname)) {
    if (workspaceLoading) return <Splash>Opening your trove…</Splash>;
    return <SetupGate initialView={pathname === '/recovery' ? 'phrase' : 'choose'} />;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
